import { Injectable } from '@angular/core';
import { IVersionService } from './eol.interfaces';

@Injectable({
  providedIn: 'root',
})
export class VersionService implements IVersionService {
  private readonly TECH_NAME_MAPPING: { [key: string]: string } = {
    java: 'amazon-corretto',
  };

  normalizeTechName(techName: string): string {
    const normalized = techName.toLowerCase().trim();
    return this.TECH_NAME_MAPPING[normalized] || normalized;
  }

  getRecommendedVersion(
    techName: string,
    eolStack: any[],
    currentVersion?: string,
  ): string {
    if (!this.isValidInput(techName, eolStack)) {
      return 'N/A';
    }

    const normalizedTechName = this.normalizeTechName(techName);
    const versions = this.findVersionsForTech(normalizedTechName, eolStack);

    if (!versions || versions.length === 0) {
      return 'N/A';
    }

    const recommendedVersion = this.findRecommendedVersion(
      versions,
      normalizedTechName,
    );

    return this.compareVersions(
      currentVersion,
      recommendedVersion,
      normalizedTechName,
    );
  }

  extractVersionNumber(version: string, techName: string): string {
    if (!version) return 'N/A';

    const parts = version.split('.');

    // Python and Spring Boot rely on major.minor cycles (e.g. 3.11, 3.5).
    if (
      (techName === 'python' || techName === 'springboot') &&
      parts.length >= 2
    ) {
      return `${parts[0]}.${parts[1]}`;
    }

    // Other technologies use only major version
    return parts[0];
  }

  compareVersions(
    currentVersion: string | undefined,
    recommendedMajor: string,
    techName: string,
  ): string {
    if (!currentVersion) return recommendedMajor;

    const currentMajor = this.extractVersionNumber(currentVersion, techName);
    return currentMajor === recommendedMajor ? 'Up to date' : recommendedMajor;
  }

  private isValidInput(techName: string, eolStack: any[]): boolean {
    return !!(eolStack && Array.isArray(eolStack) && techName);
  }

  private findVersionsForTech(
    normalizedTechName: string,
    eolStack: any[],
  ): any[] | null {
    for (const stackItem of eolStack) {
      const techKey = Object.keys(stackItem).find(
        (key) => key.toLowerCase() === normalizedTechName,
      );

      if (
        techKey &&
        Array.isArray(stackItem[techKey]) &&
        stackItem[techKey].length > 0
      ) {
        return stackItem[techKey];
      }
    }
    return null;
  }

  private findRecommendedVersion(
    versions: any[],
    normalizedTechName: string,
  ): string {
    const ltsVersion = this.findLTSVersion(versions);

    if (ltsVersion && ltsVersion.latest) {
      return this.extractVersionNumber(ltsVersion.latest, normalizedTechName);
    }

    const latestVersion = versions[0]?.latest;
    if (!latestVersion) return 'N/A';

    return this.extractVersionNumber(latestVersion, normalizedTechName);
  }

  private findLTSVersion(versions: any[]): any | null {
    return (
      versions.find(
        (version: any) =>
          version.lts === true ||
          (typeof version.lts === 'string' && version.lts.trim() !== ''),
      ) || null
    );
  }

  getNextVersion(
    techName: string,
    eolStack: any[],
    currentVersion?: string,
  ): string | null {
    if (!this.isValidInput(techName, eolStack) || !currentVersion) {
      return null;
    }

    const normalizedTechName = this.normalizeTechName(techName);
    const versions = this.findVersionsForTech(normalizedTechName, eolStack);

    if (!versions || versions.length === 0) {
      return null;
    }

    const currentExtracted = this.extractVersionNumber(
      currentVersion,
      normalizedTechName,
    );

    // Sort cycles ascending numerically (handles "22", "3.11", etc.)
    const sortedCycles = versions
      .map((v: any) => v.cycle as string)
      .filter((c: string) => !!c)
      .sort((a: string, b: string) => {
        const [aMaj, aMin = 0] = a.split('.').map(Number);
        const [bMaj, bMin = 0] = b.split('.').map(Number);
        return aMaj !== bMaj ? aMaj - bMaj : aMin - bMin;
      });

    const idx = sortedCycles.findIndex((c: string) => c === currentExtracted);

    if (idx === -1 || idx === sortedCycles.length - 1) {
      return null;
    }

    return sortedCycles[idx + 1];
  }
}
